<script setup>
import { divIcon, latLngBounds, marker } from 'leaflet'
import {
  LMap,
  LMarker,
  LTileLayer,
  LControlScale,
} from '@vue-leaflet/vue-leaflet'
import { LMarkerClusterGroup } from 'vue-leaflet-markercluster'
import IndexMapLocation from './IndexMapLocation.vue'

import { useI18n } from 'vue-i18n'
import { computed, onMounted, reactive, ref, nextTick, watch } from 'vue'
import { map } from 'leaflet'

const { t } = useI18n()
const props = defineProps(['data'])
const active = ref(null)
const mapInstance = ref(null)
const userMarker = ref(null)
const hasMapMoved = ref(false)

const options = {
  tile: {
    url: 'https://tiles.celebrare-timisoara.ro/styles/city-celebration-2025/256/{z}/{x}/{y}.png',
    // url: 'https://tiles.celebrare-timisoara.ro/styles/osm-bright/256/{z}/{x}/{y}.png',
    // url: 'https://tiles.stadiamaps.com/tiles/osm_bright/{z}/{x}/{y}{r}.png',
    maxZoom: 20,

    attribution:
      '<a href="https://www.maptiler.com/copyright/" target="_blank">&copy; MapTiler</a> <a href="https://www.openstreetmap.org/copyright" target="_blank">&copy; OpenStreetMap</a>',
    // detectRetina: true,
  },
  map: {
    zoomSnap: 1.25,
    minZoom: 13,
    maxZoom: 19,
    zoomControl: false,
    // Fix mobile scrolling - require two fingers for map interaction
    dragging: true,
    touchZoom: 'center',
    doubleClickZoom: true,
    scrollWheelZoom: false,
    tap: true,
    tapTolerance: 15,
  },
  cluster: {
    spiderfyDistanceMultiplier: 2.5,
    showCoverageOnHover: false,
    zoomToBoundsOnClick: true,
    iconCreateFunction: (cluster) => {
      const count = cluster
        .getAllChildMarkers()
        .reduce((sum, value) => sum + value.options.icon.options.count, 0)

      return getIcon(count, [64, 64], 'is-cluster')
    },
  },
}

const openLocation = (location) => {
  active.value = location
}

const getIcon = (count, iconSize, className) => {
  return divIcon({
    html: `<p class="is-circle">${count}</p>`,
    iconSize,
    iconAnchor: iconSize.map((e) => e / 2),
    className,
    count,
  })
}

const bounds = computed(() => {
  return props.data.locations.length
    ? new latLngBounds(props.data.locations.map((e) => [e.lat, e.long]))
    : new latLngBounds([
        [45.735375179956255, 21.18930357280082],
        [45.769772224274595, 21.263830647332476],
      ])
})

const getUserLocation = () => {
  if (mapInstance.value) {
    nextTick(() => {
      mapInstance.value.locate({
        watch: false,
        setView: true,
        maxZoom: 15,
      })

      mapInstance.value.once('locationfound', (e) => {
        if (userMarker.value) {
          mapInstance.value.removeLayer(userMarker.value)
        }

        userMarker.value = marker(e.latlng, {
          icon: getIcon('', [16, 16], 'is-user'),
          interactive: false,
        })

        userMarker.value.addTo(mapInstance.value)
      })
    })
  }
}

const onMapReady = (instance) => {
  mapInstance.value = instance
  const fitBounds = bounds.value.pad(0.15)
  instance.fitBounds(fitBounds)

  // Listen for map interactions
  instance.on('movestart', () => {
    hasMapMoved.value = true
  })

  instance.on('zoomstart', () => {
    hasMapMoved.value = true
  })
}

const resetView = () => {
  if (mapInstance.value) {
    mapInstance.value.fitBounds(bounds.value.pad(0.15))
    setTimeout(() => {
      hasMapMoved.value = false
    }, 200)
  }
}

onMounted(() => {})

watch(
  () => props.data,
  () => {
    active.value = null
  }
)
</script>

<template>
  <div id="map">
    <div class="map-buttons">
      <button type="button" class="button is-square" @click="getUserLocation">
        <span class="icon is-size-6"><b-icon icon="target" /></span>
      </button>

      <button
        type="button"
        class="button is-square is-black"
        @click="mapInstance.setZoom(mapInstance.getZoom() + 1)"
      >
        <span class="icon is-size-6"><b-icon icon="plus" /></span>
      </button>

      <button
        type="button"
        class="button is-square is-black"
        @click="mapInstance.setZoom(mapInstance.getZoom() - 1)"
      >
        <span class="icon is-size-6"><b-icon icon="minus" /></span>
      </button>
    </div>

    <button
      type="button"
      class="button is-small is-black is-absolute"
      @click="resetView"
      :class="{
        'is-invisible': !mapInstance || !hasMapMoved,
      }"
    >
      Resetează
    </button>

    <l-map
      v-bind="{
        options: options.map,
        maxBounds: bounds.pad(0.5),
      }"
      @ready="onMapReady"
      v-if="bounds"
    >
      <l-tile-layer
        v-bind="options.tile"
        layer-type="base"
        name="OpenStreetMap"
      />

      <l-control-scale position="bottomleft" :imperial="false" />

      <l-marker-cluster-group v-bind="{ ...options.cluster }">
        <l-marker
          v-for="marker in data.locations"
          :key="'marker-' + marker._id"
          :icon="
            getIcon(
              marker.count,
              [48, 48],
              marker._id == active?._id ? 'is-active' : ''
            )
          "
          :lat-lng="[marker.lat, marker.long]"
          @click="openLocation(marker)"
        />
      </l-marker-cluster-group>
    </l-map>
  </div>

  <IndexMapLocation v-bind="{ active }" @close="active = null" />
</template>
